import { useState, useEffect, memo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ForkKnife } from "@phosphor-icons/react";
import { optimizeImageUrl } from "@/utils/imageOptimizer";

const _titleCase = (name) =>
  (name || "").replace(/\b\w/g, (c) => c.toUpperCase());

// Isolated card so React skips re-rendering every sibling when one's state
// changes. No framer-motion, no entrance animation — clicks must feel instant
// on low-end terminal CPUs (i5 / no dGPU / 8GB RAM).
const MenuCard = memo(({ item, onItemClick, compact }) => {
  return (
    <div
      onClick={() => onItemClick(item)}
      className={compact ? "bg-white rounded-md border border-border p-1.5 cursor-pointer" : "menu-item group"}
      data-testid={`menu-item-${item.id}`}
    >
      {/* Staff POS: square image sized to card width (~110px, matches the
          width of "Chocolate Lava" text). Customer / QR ordering keeps
          its own larger marketing images in SelfOrderPage. */}
      <div className={`${compact ? "aspect-[16/5]" : "aspect-square"} rounded-md overflow-hidden bg-secondary ${compact ? "mb-1" : "mb-1.5"}`}>
        {item.image_url ? (
          <img
            src={optimizeImageUrl(item.image_url, compact ? 180 : 280)}
            alt={item.name}
            loading="lazy"
            decoding="async"
            width={compact ? 180 : 280}
            height={compact ? 56 : 140}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1734330932655-e6f3e7aff297?w=300&q=70&fit=crop&auto=format"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><ForkKnife size={20} className="text-muted-foreground/30" /></div>
        )}
      </div>
      {/* Staff POS: card width is intentionally narrow (matches the width
          of "Chocolate Lava" at text-xs). Longer item names wrap onto a
          second line via line-clamp-2. Server knows the menu — no
          description or option count needed. */}
      <div>
        <h3 className="font-medium text-xs leading-tight line-clamp-2 mb-0.5 break-words">{item.name}</h3>
        <p className="font-mono font-medium text-primary text-xs">${item.price.toFixed(2)}</p>
      </div>
    </div>
  );
});
MenuCard.displayName = "MenuCard";

const MenuGrid = ({ items, onItemClick, compact = false }) => {
  // Categories are now dynamic (any name). Fetched once on mount from
  // ``/api/menu-categories``. We ALWAYS union that response with the
  // distinct categories actually present on the loaded items — this way
  // the grid never hides items when the categories collection is out of
  // sync (partial migration, direct-DB import, etc.).
  const [apiCategories, setApiCategories] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/menu-categories`).then(({ data }) => {
      if (!cancelled) setApiCategories(data || []);
    }).catch(() => {
      if (!cancelled) setApiCategories([]);
    });
    return () => { cancelled = true; };
  }, []);

  // Merge API-registered categories with categories present on items.
  // Comparison is case-insensitive so "Mains" (item) and "mains"
  // (registered) collapse to one tab.
  const categories = (() => {
    const seen = new Map(); // key: lowercase, value: {name, sort_order}
    (apiCategories || []).forEach((c) => {
      const key = (c.name || "").toLowerCase();
      if (key && !seen.has(key)) seen.set(key, { name: c.name, sort_order: c.sort_order ?? 999 });
    });
    (items || []).forEach((it) => {
      const key = (it.category || "").toLowerCase();
      if (!key || seen.has(key)) return;
      seen.set(key, { name: it.category, sort_order: 999 });
    });
    return Array.from(seen.values()).sort((a, b) => {
      if ((a.sort_order ?? 999) !== (b.sort_order ?? 999)) return (a.sort_order ?? 999) - (b.sort_order ?? 999);
      return (a.name || "").localeCompare(b.name || "");
    });
  })();

  const [activeCategory, setActiveCategory] = useState(null);
  useEffect(() => {
    if (activeCategory) return;
    if (categories.length > 0) setActiveCategory(categories[0].name);
  }, [categories, activeCategory]);

  // Case-insensitive filter — an item's category string may not exactly
  // match the tab label (e.g. "Mains" vs "mains" from a partial import).
  const activeKey = (activeCategory || "").toLowerCase();
  const filteredItems = items.filter(item => (item.category || "").toLowerCase() === activeKey);
  const availableItems = filteredItems.filter(item => item.available);
  // Alphabetize by default; a manual sort_order (set by admin drag in
  // Settings → Menu) always wins over the alphabetical fallback so the
  // owner can promote signature items to the front.
  availableItems.sort((a, b) => {
    const ao = a.sort_order ?? Number.POSITIVE_INFINITY;
    const bo = b.sort_order ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <div data-testid="menu-grid">
      <Tabs value={activeCategory || ""} onValueChange={setActiveCategory} className={compact ? "mb-3" : "mb-6"}>
        <TabsList className={`bg-secondary/50 h-auto p-1 flex-wrap ${compact ? "gap-0.5" : ""}`}>
          {categories.map((cat) => (
            <TabsTrigger
              key={cat.name}
              value={cat.name}
              className={`flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm ${compact ? "px-3 py-2" : "px-4 py-2"}`}
              data-testid={`category-${cat.name}`}
            >
              <ForkKnife size={compact ? 16 : 18} />
              <span className={compact ? "text-xs" : "hidden sm:inline"}>{_titleCase(cat.name)}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className={compact ? "grid grid-cols-4 gap-1.5" : "menu-grid"}>
        {availableItems.map((item) => (
          <MenuCard key={item.id} item={item} onItemClick={onItemClick} compact={compact} />
        ))}
      </div>

      {availableItems.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <ForkKnife size={48} className="mx-auto mb-2 opacity-30" /><p>No items in this category</p>
        </div>
      )}
    </div>
  );
};

export default memo(MenuGrid);
